import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Award, Shield, Heart, BookOpen, Dumbbell, Leaf, Users, GraduationCap, Flame, ExternalLink, X } from "lucide-react";
import { useSiteContent } from "@/hooks/useContent";

const ICON_ROTATION = [Flame, GraduationCap, Award, Dumbbell, Leaf, Users, Shield, Heart, BookOpen];

function QualCard({ q, i }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON_ROTATION[i % ICON_ROTATION.length];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, delay: i * 0.06 }}
        whileHover={{ y: -3, scale: 1.01 }}
        onClick={() => setOpen(true)}
        className={`${q.span || ""} border border-white/10 p-5 md:p-7 rounded-lg group bg-gg-black/50 cursor-pointer hover-glow transition-shadow duration-300 relative overflow-hidden`}
        data-testid={`qualification-card-${i}`}>
        <div className="flex items-start justify-between">
          <Icon className="w-5 h-5 text-gg-red mb-3 group-hover:scale-125 group-hover:rotate-6 transition-all duration-300" />
          <ExternalLink size={12} className="text-zinc-600 group-hover:text-gg-red transition-colors" />
        </div>
        <h3 className="font-heading text-sm md:text-base uppercase tracking-wider font-bold text-white group-hover:text-gg-red transition-colors duration-300">{q.title}</h3>
        <p className="text-zinc-500 text-xs mt-2 tracking-wide leading-relaxed">{q.desc}</p>
        <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gg-red group-hover:w-full transition-all duration-500 rounded-full" />
      </motion.div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-gg-surface border border-white/10 rounded-lg p-8 max-w-md w-full relative">
              <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18} /></button>
              <Icon className="w-8 h-8 text-gg-red mb-4" />
              <h3 className="font-heading text-xl uppercase tracking-wider font-bold text-white">{q.title}</h3>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{q.desc}</p>
              {q.detail && <p className="text-zinc-300 text-sm mt-4 leading-relaxed border-t border-white/10 pt-4">{q.detail}</p>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function Qualifications() {
  const { content } = useSiteContent();
  const quals = content?.qualifications || [];

  if (quals.length === 0) return null;

  return (
    <section id="qualifications" className="py-8 md:py-10 bg-gg-surface" data-testid="qualifications-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <p className="text-zinc-500 text-sm tracking-wide mb-8">Click any card to learn more</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {quals.map((q, i) => <QualCard key={i} q={q} i={i} />)}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="mt-8 border-l-2 border-gg-red pl-6 rounded-l">
          <p className="text-zinc-400 text-sm italic tracking-wide">"I continue to study nutrition science and exercise science in depth to keep myself up to date in the field — because your results depend on it."</p>
          <span className="text-white font-heading uppercase tracking-widest text-xs mt-2 block">— {content?.about?.name || "Gora Ganguly"}</span>
        </motion.div>
      </div>
    </section>
  );
}
